import Swal from "sweetalert2";
import {
  fetchFileuploadPresignedUrl,
  getFileuploadDisplayName,
  openFileupload,
  showFileuploadAccessError,
} from "@/utils/fileuploadAccess";

/** Value for `<input type="file" accept="...">` (matches allowed MIME + extensions). */
export const SUPPORT_TICKET_FILE_INPUT_ACCEPT =
  "image/*,video/*,application/pdf,.xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/** Aligned with back/routes/uploadRoutes.js allowedMimeTypes */
export const SUPPORT_TICKET_UPLOAD_MIME_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "application/pdf",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "video/mp4",
  "video/webm",
  "video/quicktime",
  "video/x-msvideo",
  "video/ogg",
  "video/3gpp",
];

const SUPPORT_TICKET_VIDEO_EXTENSIONS = ["mp4", "webm", "mov", "m4v", "avi", "ogv", "3gp"];

/** Match multer limits in uploadRoutes.js */
export const SUPPORT_TICKET_MAX_FILE_BYTES = 20 * 1024 * 1024;

const DEFAULT_413_URL = "https://api2.delegat.es/api/upload/upload";

export function resolveUploadRequestUrl(error) {
  const cfg = error && error.config;
  if (!cfg) return DEFAULT_413_URL;
  const path = cfg.url || "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  const base = (cfg.baseURL || "").replace(/\/+$/, "");
  if (!base) return DEFAULT_413_URL;
  return `${base}/${path.replace(/^\//, "")}`;
}

/**
 * Dev-friendly copy (similar to Chrome DevTools) when the proxy/server rejects the body size.
 */
export function formatSupportUpload413Message(error) {
  const requestUrl = resolveUploadRequestUrl(error);
  return [
    "Request URL",
    requestUrl,
    "Request Method",
    "POST",
    "Status Code",
    "413 Request Entity Too Large",
  ].join("\n");
}

export function isSupportTicketAttachmentAllowed(file) {
  if (!file) return false;
  if (SUPPORT_TICKET_UPLOAD_MIME_TYPES.includes(file.type)) return true;
  if (String(file.type || "").startsWith("video/")) return true;
  const name = (file.name || "").toLowerCase();
  return (
    /\.(jpe?g|png|gif)$/i.test(name) ||
    /\.(mp4|webm|mov|m4v|avi|ogv|3gp)$/i.test(name) ||
    name.endsWith(".pdf") ||
    name.endsWith(".xlsx") ||
    name.endsWith(".xls")
  );
}

export function showSupportUpload413Dialog(vm, error) {
  Swal.fire({
    icon: "error",
    title: vm.$tn("Request entity too large"),
    text: formatSupportUpload413Message(error),
    width: 640,
  });
}

export function isUploadPayloadTooLarge(error) {
  return error && error.response && error.response.status === 413;
}

export function getSupportAttachmentExtension(fileKey) {
  const name = getFileuploadDisplayName(fileKey) || String(fileKey || "");
  const match = name.match(/\.([a-z0-9]+)$/i);
  return match ? match[1].toLowerCase() : "";
}

export function isSupportAttachmentImage(fileKey) {
  const ext = getSupportAttachmentExtension(fileKey);
  return ["jpg", "jpeg", "png", "gif", "webp", "bmp"].includes(ext);
}

export function isSupportAttachmentPdf(fileKey) {
  return getSupportAttachmentExtension(fileKey) === "pdf";
}

export function isSupportAttachmentExcel(fileKey) {
  const ext = getSupportAttachmentExtension(fileKey);
  return ext === "xls" || ext === "xlsx";
}

export function isSupportAttachmentVideo(fileKey) {
  const ext = getSupportAttachmentExtension(fileKey);
  return SUPPORT_TICKET_VIDEO_EXTENSIONS.includes(ext);
}

export function getSupportAttachmentIconClass(fileKey) {
  if (isSupportAttachmentImage(fileKey)) return "fa-file-image text-primary";
  if (isSupportAttachmentVideo(fileKey)) return "fa-file-video text-info";
  if (isSupportAttachmentPdf(fileKey)) return "fa-file-pdf text-danger";
  if (isSupportAttachmentExcel(fileKey)) return "fa-file-excel text-success";
  return "fa-file text-secondary";
}

export function getSupportAttachmentDisplayName(fileKey) {
  return getFileuploadDisplayName(fileKey) || String(fileKey || "");
}

/** Las URLs prefirmadas duran 1h en el back; refrescamos antes para evitar 403. */
const PREVIEW_URL_TTL_MS = 45 * 60 * 1000;
const previewUrlCache = new Map();

/**
 * URL prefirmada cacheada para mostrar miniaturas sin repetir requests.
 * @param {string} fileKey
 * @returns {Promise<string|null>}
 */
export function getSupportAttachmentPreviewUrl(fileKey) {
  if (!fileKey) return Promise.resolve(null);
  const cached = previewUrlCache.get(fileKey);
  if (cached && Date.now() - cached.ts < PREVIEW_URL_TTL_MS) return cached.promise;

  const entry = {
    ts: Date.now(),
    promise: fetchFileuploadPresignedUrl(fileKey).catch(() => {
      previewUrlCache.delete(fileKey);
      return null;
    }),
  };
  previewUrlCache.set(fileKey, entry);
  return entry.promise;
}

/**
 * @param {string} fileKey
 * @param {object} vm Vue instance ($tn)
 * @param {{ previewUrl?: string, onImage?: (url: string, fileKey: string) => void }} [options]
 */
export async function openSupportAttachment(fileKey, vm, options = {}) {
  if (!fileKey) return;

  if (isSupportAttachmentImage(fileKey) || isSupportAttachmentVideo(fileKey)) {
    try {
      const url =
        options.previewUrl || (await fetchFileuploadPresignedUrl(fileKey));
      if (url && typeof options.onImage === "function") {
        options.onImage(url, fileKey);
        return;
      }
      if (url) {
        window.open(url, "_blank");
      } else {
        showFileuploadAccessError(vm, vm.$tn("Error accessing file."), {
          fileRef: fileKey,
        });
      }
    } catch (error) {
      showFileuploadAccessError(
        vm,
        error?.response?.data?.message || vm.$tn("Error accessing file."),
        { fileRef: fileKey }
      );
    }
    return;
  }

  const result = await openFileupload(fileKey, { t: (k) => vm.$tn(k) });
  if (!result.ok) {
    showFileuploadAccessError(vm, result.errorMessage, {
      title: vm.$tn("Error accessing file."),
      fileRef: fileKey,
    });
  }
}
